import { graphql } from "gatsby"
import Autoplay from "embla-carousel-autoplay"
import useEmblaCarousel from "embla-carousel-react"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { CarouselSlideFieldsFragment } from "../../../../graphql-types"
import { pushGtmEvent } from "../../../gtm/interaction-event"
import { useInternalExternalLink } from "../../../hooks"
import * as styles from "./carousel.module.scss"

type RenderProps = {
  slides: CarouselSlideFieldsFragment[]
  label: string
  previousLabel: string
  nextLabel: string
  pauseLabel: string
  playLabel: string
  locale: string
  defaultLocale: string
}

const Carousel: React.FC<RenderProps> = ({ slides, label, locale, defaultLocale }) => {
  const autoplay = useMemo(() => Autoplay({ delay: 5000, stopOnInteraction: false, stopOnMouseEnter: true }), [])
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true }, [autoplay])
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (!emblaApi) {
      return
    }
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap())
    emblaApi.on("select", onSelect)
    emblaApi.on("reInit", onSelect)
    return () => {
      emblaApi.off("select", onSelect)
      emblaApi.off("reInit", onSelect)
    }
  }, [emblaApi])

  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi])

  return (
    <div className={styles.carousel}>
      <div className={styles.controls}>
        {slides.map((slide, index) => (
          <button
            key={index}
            type="button"
            className={`${styles.dot} ${index === selectedIndex ? styles.dotSelected : ""}`}
            aria-label={slide.image.alt || `${label} ${index + 1}`}
            aria-current={index === selectedIndex ? "true" : undefined}
            onClick={() => {
              scrollTo(index)
              pushGtmEvent("eventga", "Homepage", "Carousel", "Scroll Button Click")
            }}
          />
        ))}
      </div>

      <div
        ref={emblaRef}
        className={styles.sliderTray}
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
      >
        <div className={styles.slideTray}>
          {slides.map((slide, index) => {
            const link = useInternalExternalLink(slide.internalExternalLink[0], locale, defaultLocale)
            return (
              <div className={styles.slide} key={index} role="group" aria-roledescription="slide">
                <a href={link} onClick={() => pushGtmEvent("eventga", "Homepage", "Carousel", link)}>
                  <img src={slide.image.url} alt={slide.image.alt || ""} />
                </a>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export const fragment = graphql`
  fragment CarouselSlideFields on DatoCmsSlide {
    image {
      alt
      url
    }
    internalExternalLink {
      ...InternalExternalLinkFields
    }
  }
`

export default Carousel
